import { ROUTES } from "@/routes"
import { useSetupStore } from "@/store"
import { AlertTriangle, X } from "lucide-react"
import React, { useState } from "react"
import { Link, useLocation } from "react-router-dom"

/**
 * AwsCredentialsBanner
 *
 * Site-wide banner shown to a superadmin whenever their stored AWS
 * credentials are known to have failed re-validation (aws_validation_status
 * === "failed"). Purely reads from useSetupStore — it does not poll or
 * trigger any AWS call itself. The status it displays is only as fresh as
 * the last on-demand check (Settings > AWS Configuration mount, or the
 * "Recheck Now" button there) — see AwsConfigurationSettings.tsx.
 */
export const AwsCredentialsBanner: React.FC = () => {
  const accountStatus = useSetupStore((state) => state.accountStatus)
  const [dismissed, setDismissed] = useState(false)
  const location = useLocation()

  const shouldShow =
    accountStatus?.user_type === "superadmin" &&
    accountStatus.has_aws_credentials === true &&
    accountStatus.aws_validation_status === "failed"

  // Re-arm the dismiss state on navigation so the warning isn't permanently
  // hidden for the rest of the session after a single dismissal.
  React.useEffect(() => {
    setDismissed(false)
  }, [location.pathname])

  if (!shouldShow || dismissed) return null

  const onSettingsPage = location.pathname === ROUTES.account.settings.path

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="fixed top-0 left-0 right-0 z-[9999] border-b border-amber-700 bg-amber-600 text-white shadow-[0_4px_16px_rgba(0,0,0,0.25)]"
    >
      <div className="mx-auto flex max-w-7xl items-start justify-between gap-4 px-4 py-3 sm:items-center">
        <div className="flex items-start gap-3 sm:items-center">
          <div className="mt-0.5 shrink-0 sm:mt-0">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-3">
            <span className="text-sm font-semibold">Your AWS credentials are no longer valid</span>
            <span className="text-xs text-amber-100 sm:text-sm">
              AWS rejected your stored access key. Reconnect it to keep using AWS-connected features.
            </span>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          {!onSettingsPage && (
            <Link
              to={ROUTES.account.settings.path}
              className="flex items-center gap-1.5 rounded-md border border-amber-400 bg-amber-700 px-3 py-1.5 text-xs font-medium hover:bg-amber-800 focus:outline-none focus:ring-2 focus:ring-white transition-colors"
            >
              Fix in Settings
            </Link>
          )}

          <button
            onClick={() => setDismissed(true)}
            aria-label="Dismiss"
            className="rounded p-1 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-white transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default AwsCredentialsBanner
